// Admin, alertas (croqui `screens/admin/alertas.tsx`, `spec/17` §8.2). Foco: **a lista
// de alertas ativos**.
//
// `POST /admin/v1/system/alerts/{name}/ack` silencia por 24 h e a chave é o NOME, não um
// id (`spec/17` §8.3, `inventario-admin.md`). Depois do `204` as duas keys de WEB-20 são
// invalidadas: o contador de alertas críticos da visão geral sai deste mesmo recurso.
//
// O detalhe é um drawer COM URL (`?alert=&entity=`), não uma rota `$id`: alerta não tem
// id, e a rota-filha ficaria fora de `files_allowed` (ver "Questões abertas" da WEB-70).

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { errorMessageOf } from '../api/errors.ts';
import {
  type AdminAlert,
  ALERT_ACK_INVALIDATES,
  ackAlert,
  adminAlertsQueryOptions,
} from '../api/queries/admin-overview.ts';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { AlertDetailDrawer } from '../features/admin-overview/alert-detail.tsx';
import {
  AlertsPanels,
  AlertsSummary,
  alertRowKey,
} from '../features/admin-overview/alerts-list.tsx';

interface AlertsSearch {
  alert?: string;
  entity?: string;
}

function validateSearch(search: Record<string, unknown>): AlertsSearch {
  const name = typeof search.alert === 'string' && search.alert !== '' ? search.alert : undefined;
  if (name === undefined) {
    return {};
  }
  const entity = typeof search.entity === 'string' ? search.entity : undefined;
  return entity === undefined ? { alert: name } : { alert: name, entity };
}

function AdminAlerts() {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const { alert: openName, entity: openEntity } = Route.useSearch();

  const alerts = useQuery(adminAlertsQueryOptions());
  const rows = alerts.data?.data ?? [];

  const [confirming, setConfirming] = useState<AdminAlert | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const open =
    openName === undefined
      ? undefined
      : rows.find(
          (row) => row.name === openName && row.entity_key === (openEntity ?? row.entity_key),
        );

  const openDetail = (alert: AdminAlert): void => {
    void navigate({
      search:
        alert.entity_key === ''
          ? { alert: alert.name }
          : { alert: alert.name, entity: alert.entity_key },
      replace: true,
    });
  };

  const closeDetail = (): void => {
    void navigate({ search: {}, replace: true });
  };

  const confirm = async (): Promise<void> => {
    if (confirming === undefined) {
      return;
    }
    setBusy(true);
    setFailure(undefined);
    try {
      await ackAlert(confirming.name);
      await Promise.all(
        ALERT_ACK_INVALIDATES.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      setConfirming(undefined);
    } catch (caught) {
      setFailure(errorMessageOf(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {alerts.data !== undefined && <AlertsSummary alerts={rows} />}

      <AlertsPanels
        alerts={rows}
        isPending={alerts.isPending}
        error={alerts.error}
        onRetry={() => {
          void alerts.refetch();
        }}
        onOpen={openDetail}
        onAck={(alert) => {
          setFailure(undefined);
          setConfirming(alert);
        }}
        {...(open === undefined ? {} : { selectedKey: alertRowKey(open) })}
      />

      <AlertDetailDrawer
        alert={open}
        onClose={closeDetail}
        busy={busy}
        onAck={(alert) => {
          setFailure(undefined);
          setConfirming(alert);
        }}
      />

      {/* O croqui desenha a confirmação como um segundo `Drawer`; aqui é o `Dialog` do
          shadcn para não empilhar dois `vaul` (ver "Questões abertas"). */}
      <Dialog
        open={confirming !== undefined}
        onOpenChange={(next) => {
          if (!next && !busy) {
            setConfirming(undefined);
          }
        }}
      >
        <DialogContent className="bg-raised sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Silenciar por 24 horas?</DialogTitle>
            <DialogDescription>
              O alerta <span className="font-mono">{confirming?.name}</span> para de notificar por
              24 h e continua na lista, marcado como silenciado. O silêncio vale para todas as
              linhas com esse nome e fica na trilha de auditoria.
            </DialogDescription>
          </DialogHeader>
          {failure !== undefined && (
            <p role="alert" className="text-11 text-danger">
              {failure}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setConfirming(undefined);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                void confirm();
              }}
            >
              {busy ? 'Silenciando…' : 'Silenciar 24 h'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const Route = createFileRoute('/admin/alerts/')({ component: AdminAlerts, validateSearch });
